import React, { useState, useEffect, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

const CATEGORIES = [
  "Accounting & Tax",
  "SMSF",
  "Documents",
  "Management",
  "Collaboration",
];

const TechnologySlider = () => {
  const [technologies, setTechnologies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");

  const api_url =
    localStorage.getItem("api_url") || "http://localhost/inscape_backend/api/";
  const company_id = localStorage.getItem("company_id") || "1";
  const employee_id = localStorage.getItem("employee_id") || "1";

  useEffect(() => {
    fetchTechnologies();
  }, []);

  const fetchTechnologies = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("employee_id", employee_id);
      formData.append("type", "1");

      const response = await fetch(`${api_url}technology.php`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.status === "1") {
        setTechnologies(data.result || []);
        setError(null);
      } else {
        setError(data.error || "Failed to fetch technologies");
        setTechnologies([]);
      }
    } catch (err) {
      setError("Network error. Please check your connection.");
      setTechnologies([]);
    } finally {
      setLoading(false);
    }
  };

  // Build tab list: only show categories that have at least one technology
  const availableTabs = useMemo(() => {
    const populated = CATEGORIES.filter((cat) =>
      technologies.some((t) => t.technology_category === cat),
    );
    return populated.length > 0 ? ["All", ...populated] : ["All"];
  }, [technologies]);

  // Filter technologies for the active tab
  const visibleTechnologies = useMemo(() => {
    if (activeCategory === "All") return technologies;
    return technologies.filter((t) => t.technology_category === activeCategory);
  }, [technologies, activeCategory]);

  if (loading) {
    return (
      <section className="clients border-top section-full-width pt-50 pb-50">
        <div className="container-fluid">
          <div className="col-12 text-center">
            <p style={{ color: "#666" }}>Loading technologies...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || technologies.length === 0) return null;

  return (
    <section className="clients border-top section-full-width pt-50 pb-50">
      <style>{`
        .tech-tabs-wrapper {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          justify-content: center;
          margin-bottom: 32px;
        }

        .tech-tab-btn {
          position: relative;
          padding: 8px 20px;
          border: 1.5px solid #e0e0e0;
          border-radius: 10px;
          background: transparent;
          color: #666;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.25s ease;
          white-space: nowrap;
          outline: none;
          letter-spacing: 0.02em;
        }

        .tech-tab-btn:hover {
          border-color: #ff6600;
          color: #ff6600;
        }

        .tech-tab-btn.active {
          background: #ff6600;
          border-color: #ff6600;
          color: #fff;
          box-shadow: 0 4px 14px rgba(255, 102, 0, 0.35);
        }

        .tech-slider-fade {
          animation: techFadeIn 0.3s ease;
        }

        @keyframes techFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="container-fluid">
        {/* Category Tabs */}
        {availableTabs.length > 1 && (
          <div className="tech-tabs-wrapper">
            {availableTabs.map((cat) => (
              <button
                key={cat}
                className={`tech-tab-btn${activeCategory === cat ? " active" : ""}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Technology Slider */}
        <div className="row align-items-center">
          <div className="col-12">
            {visibleTechnologies.length === 0 ? (
              <p
                style={{ textAlign: "center", color: "#999", fontSize: "14px" }}
              >
                No technologies in this category.
              </p>
            ) : (
              <div className="tech-slider-fade" key={activeCategory}>
                <Swiper
                  slidesPerView={2}
                  spaceBetween={30}
                  loop={visibleTechnologies.length > 2}
                  autoplay={{
                    delay: 6000,
                    disableOnInteraction: false,
                  }}
                  breakpoints={{
                    400: { slidesPerView: 3 },
                    550: { slidesPerView: 4 },
                    992: { slidesPerView: 5 },
                    1200: { slidesPerView: 6 },
                  }}
                  modules={[Autoplay]}
                  className="swiper"
                >
                  {visibleTechnologies.map((technology) => (
                    <SwiperSlide
                      key={technology.technology_id}
                      className="client"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "80px",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: "80px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "10px 15px",
                        }}
                      >
                        <img
                          src={technology.technology_image_url}
                          alt={technology.technology_name}
                          title={technology.technology_name}
                          loading="lazy"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "60px",
                            width: "auto",
                            height: "auto",
                            objectFit: "contain",
                            display: "block",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnologySlider;
